// ServiceSuspended.js - Service Suspended Page
// Displayed when a shop's subscription is inactive or payment has failed

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShop } from './App';

const ServiceSuspended = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const shopContext = useShop();
  
  const shopInfo = location.state?.shopInfo || shopContext?.shop;
  const shopName = shopInfo?.shopName || 'This shop';

  // Square Customer Portal URL (shop owners can update payment here)
  const getSquarePortalUrl = () => {
    if (shopInfo?.squareCustomerId) {
      // In production, this would link to Square's customer portal
      // or your own payment update page
      return `https://squareup.com/dashboard/customers/${shopInfo.squareCustomerId}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 via-slate-900 to-slate-900 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-lg w-full">
        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white">Service Suspended</h1>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            <div className="text-center mb-8">
              <p className="text-lg text-slate-700 mb-2">
                <span className="font-semibold">{shopName}</span>'s digital intake service has been temporarily suspended.
              </p>
              <p className="text-slate-500">
                This is usually due to a payment issue with the subscription.
              </p>
            </div>

            {/* Status Box */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-red-800">Subscription Issue</h3>
                  <p className="text-sm text-red-600 mt-1">
                    {shopInfo?.suspensionReason || 'Payment method needs to be updated to restore service.'}
                  </p>
                </div>
              </div>
            </div>

            {/* For Customers */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                For Customers
              </h3>
              <p className="text-slate-600 text-sm">
                Please check in at the service counter. The shop can still help you, but digital check-in is temporarily unavailable.
              </p>
            </div>

            {/* For Shop Owners */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                For Shop Owners
              </h3>
              
              <div className="space-y-3">
                {getSquarePortalUrl() ? (
                  <a
                    href={getSquarePortalUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.8c1.32 0 2.4 1.08 2.4 2.4v1.2h-4.8V7.2c0-1.32 1.08-2.4 2.4-2.4zm4.8 14.4H7.2v-9.6h9.6v9.6z"/>
                    </svg>
                    <span>Update Payment in Square</span>
                  </a>
                ) : (
                  <button
                    onClick={() => navigate('/onboarding')}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <span>Subscribe to Restore Service</span>
                  </button>
                )}
                
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-6 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all font-medium"
                >
                  Shop Owner Login
                </button>

                <p className="text-xs text-slate-500 text-center mt-4">
                  Service will be restored automatically once payment is successful.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Link */}
        <div className="text-center mt-6">
          <p className="text-slate-400 text-sm">
            Need help? Contact{' '}
            <a href="mailto:support@auth-hook.com" className="text-amber-400 hover:text-amber-300">
              support@auth-hook.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceSuspended;
